import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, Copy, Trash2, CheckCircle2, XCircle, Plus, Eye, Image as ImageIcon, Download, Star, Flame } from "lucide-react";

function downloadCsv(url, filename) {
  api.get(url, { responseType: "blob" }).then((r) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
    a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  });
}

export default function AdminOffers() {
  const [offers, setOffers] = useState([]);
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [pinnedId, setPinnedId] = useState(null);
  const navigate = useNavigate();
  const load = () => {
    api.get(`/admin/offers${status?`?status=${status}`:""}`).then((r) => { setOffers(r.data); setSelected(new Set()); });
    api.get(`/admin/deal-of-day`).then((r) => setPinnedId(r.data?.pinned_offer_id || null)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const moderate = async (id, s) => { await api.patch(`/admin/offers/${id}/moderate`, { status: s }); toast.success("Modéré"); load(); };
  const toggleSel = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = offers.length > 0 && selected.size === offers.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(offers.map((o) => o.id)));
  const bulk = async (action) => {
    const labels = { published: "publier", rejected: "refuser", suspended: "suspendre", delete: "supprimer" };
    if (action === "delete" && !confirm(`Supprimer définitivement ${selected.size} offre(s) ?`)) return;
    try {
      const { data } = await api.post("/admin/offers/bulk", { ids: [...selected], action });
      toast.success(`${data.updated ?? data.deleted ?? selected.size} offre(s) : ${labels[action]} ✓`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
  };
  const toggleFeature = async (o) => {
    try {
      if (o.is_featured) {
        await api.delete(`/admin/offers/${o.id}/feature`);
        toast.success("Retiré de la une");
      } else {
        await api.post(`/admin/offers/${o.id}/feature`);
        toast.success("Mis à la une (remplace l'offre précédente)");
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };
  const togglePin = async (o) => {
    try {
      if (pinnedId === o.id) {
        await api.delete(`/admin/deal-of-day`);
        toast.success("Bon plan du jour retiré — tirage au hasard rétabli");
      } else {
        await api.post(`/admin/offers/${o.id}/pin-deal-of-day`);
        toast.success("Épinglé comme bon plan du jour (aujourd'hui)");
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };
  const del = async (id) => { if (!confirm("Supprimer ?")) return; await api.delete(`/admin/offers/${id}`); toast.success("Supprimé"); load(); };
  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/admin/offers/${id}/duplicate`);
      toast.success("Offre dupliquée");
      navigate(`/admin/offres/${data.id}/editer`);
    } catch { toast.error("Impossible de dupliquer"); }
  };
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl md:text-3xl font-bold">Offres</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/admin/offres/nouvelle">
            <Button className="btn-shine text-white" data-testid="admin-new-offer-btn"><Plus size={16} className="mr-1"/> Créer une offre</Button>
          </Link>
          <Button variant="outline" onClick={()=>downloadCsv(`/admin/offers-export${status?`?status=${status}`:""}`, "offres.csv")} data-testid="offers-export-btn">
            <Download size={14} className="mr-1"/> Export
          </Button>
          <Select value={status} onValueChange={(v)=>setStatus(v==="all"?"":v)}>
            <SelectTrigger className="w-48" data-testid="admin-offer-status-filter"><SelectValue placeholder="Tous statuts"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="published">Publiées</SelectItem>
              <SelectItem value="rejected">Refusées</SelectItem>
              <SelectItem value="suspended">Suspendues</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3" data-testid="deal-of-day-info">
        <Flame size={18} className="text-[#E05638] shrink-0 mt-0.5"/>
        <p className="text-sm text-slate-700">
          <span className="font-semibold text-[#E05638]">Bon plan du jour</span> :{" "}
          {pinnedId
            ? "une offre est épinglée manuellement pour aujourd'hui. Clique sur la flamme orange pour la retirer."
            : "aucune offre épinglée — une offre publiée est tirée au hasard automatiquement (identique toute la journée). Clique sur la flamme d'une offre pour l'épingler aujourd'hui."}
        </p>
      </div>

      <div className="mt-6 hidden md:block bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 bg-slate-900 text-white px-4 py-2.5" data-testid="offers-bulk-bar">
            <span className="text-sm font-semibold">{selected.size} sélectionnée(s)</span>
            <div className="flex-1" />
            <button onClick={()=>bulk("published")} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-xs font-semibold inline-flex items-center gap-1" data-testid="offers-bulk-publish"><CheckCircle2 size={14}/> Publier</button>
            <button onClick={()=>bulk("rejected")} className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-xs font-semibold inline-flex items-center gap-1" data-testid="offers-bulk-reject"><XCircle size={14}/> Refuser</button>
            <button onClick={()=>bulk("suspended")} className="px-3 py-1.5 rounded-lg bg-slate-600 hover:bg-slate-500 text-xs font-semibold" data-testid="offers-bulk-suspend">Suspendre</button>
            <button onClick={()=>bulk("delete")} className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-xs font-semibold inline-flex items-center gap-1" data-testid="offers-bulk-delete"><Trash2 size={14}/> Supprimer</button>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr><th className="p-3 w-10 text-center"><input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Tout sélectionner" data-testid="offers-select-all"/></th><th className="p-3 text-left w-16">Aperçu</th><th className="p-3 text-left">Titre</th><th className="p-3 text-left">Commerce</th><th className="p-3 text-left">Ville</th><th className="p-3 text-left">Réduction</th><th className="p-3 text-left">Statut</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {offers.map((o) => {
              const cover = (Array.isArray(o.images) && o.images[0]) || o.image;
              const count = Array.isArray(o.images) ? o.images.length : (o.image ? 1 : 0);
              return (
              <tr key={o.id} className={`border-t border-slate-100 ${selected.has(o.id)?"bg-amber-50":""}`} data-testid={`admin-offer-row-${o.id}`}>
                <td className="p-3 text-center"><input type="checkbox" checked={selected.has(o.id)} onChange={()=>toggleSel(o.id)} aria-label="Sélectionner" data-testid={`offer-select-${o.id}`}/></td>
                <td className="p-2">
                  <div className="relative w-14 h-14 rounded-lg overflow-hidden bg-slate-100 border border-slate-200" data-testid={`admin-offer-thumb-${o.id}`}>
                    {cover ? (
                      <img src={cover} alt={o.title} className="w-full h-full object-cover"/>
                    ) : (
                      <div className="w-full h-full grid place-items-center text-slate-300"><ImageIcon size={18}/></div>
                    )}
                    {count > 1 && (
                      <span className="absolute bottom-0 right-0 text-[10px] font-bold px-1 py-0.5 bg-black/70 text-white rounded-tl">+{count-1}</span>
                    )}
                  </div>
                </td>
                <td className="p-3 font-semibold">{o.title}</td>
                <td className="p-3">{o.business?.name}</td>
                <td className="p-3">{o.city}</td>
                <td className="p-3 font-mono">{o.discount}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    o.status === "published" ? "bg-emerald-100 text-emerald-700" :
                    o.status === "pending" ? "bg-amber-100 text-amber-700" :
                    o.status === "rejected" ? "bg-rose-100 text-rose-700" :
                    "bg-slate-100 text-slate-600"
                  }`}>{o.status}</span>
                  {o.status === "published" && (
                    (o.social_counts?.groupes || o.social_posts?.groupes)
                      ? <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`offer-groups-shared-${o.id}`} title="Partagée dans les groupes Facebook">✓ Groupes{o.social_counts?.groupes > 1 ? ` ×${o.social_counts.groupes}` : ""}</span>
                      : <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-600 border border-rose-200" data-testid={`offer-groups-pending-${o.id}`} title="Pas encore partagée dans les groupes Facebook">Groupes : à publier</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <a href={`/offre/${o.id}`} target="_blank" rel="noopener noreferrer"
                       data-testid={`admin-offer-preview-${o.id}`}
                       title="Aperçu de l'offre"
                       className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-[#E05638]">
                      <Eye size={16}/>
                    </a>
                    {o.status === "published" && (
                      <button onClick={()=>toggleFeature(o)}
                              data-testid={`admin-offer-feature-${o.id}`}
                              title={o.is_featured ? "Retirer de la une" : "Mettre à la une"}
                              className={`p-2 rounded-lg ${o.is_featured ? "text-amber-600 bg-amber-50" : "text-slate-500 hover:text-amber-600 hover:bg-amber-50"}`}>
                        <Star size={16} className={o.is_featured ? "fill-amber-500" : ""}/>
                      </button>
                    )}
                    {o.status === "published" && (
                      <button onClick={()=>togglePin(o)}
                              data-testid={`admin-offer-pin-dod-${o.id}`}
                              title={pinnedId === o.id ? "Retirer le bon plan du jour" : "Épingler comme bon plan du jour (aujourd'hui)"}
                              className={`p-2 rounded-lg ${pinnedId === o.id ? "text-[#E05638] bg-orange-50" : "text-slate-500 hover:text-[#E05638] hover:bg-orange-50"}`}>
                        <Flame size={16} className={pinnedId === o.id ? "fill-orange-500" : ""}/>
                      </button>
                    )}
                    {o.status !== "published" && (
                      <button onClick={()=>moderate(o.id, "published")}
                              data-testid={`admin-offer-approve-${o.id}`}
                              title="Publier"
                              className="p-2 rounded-lg text-emerald-700 hover:bg-emerald-50">
                        <CheckCircle2 size={16}/>
                      </button>
                    )}
                    {o.status !== "rejected" && (
                      <button onClick={()=>moderate(o.id, "rejected")}
                              title="Refuser"
                              className="p-2 rounded-lg text-amber-700 hover:bg-amber-50">
                        <XCircle size={16}/>
                      </button>
                    )}
                    <button onClick={()=>navigate(`/admin/offres/${o.id}/editer`)}
                            data-testid={`admin-offer-edit-${o.id}`}
                            title="Modifier"
                            className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900">
                      <Pencil size={16}/>
                    </button>
                    <button onClick={()=>duplicate(o.id)}
                            data-testid={`admin-offer-dup-${o.id}`}
                            title="Dupliquer"
                            className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900">
                      <Copy size={16}/>
                    </button>
                    <button onClick={()=>del(o.id)}
                            title="Supprimer"
                            className="p-2 rounded-lg text-rose-600 hover:bg-rose-50">
                      <Trash2 size={16}/>
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
            {offers.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-slate-500">Aucune offre.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Cartes mobile */}
      <div className="mt-4 md:hidden space-y-2.5">
        {offers.map((o) => {
          const cover = (Array.isArray(o.images) && o.images[0]) || o.image;
          return (
            <div key={o.id} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`admin-offer-card-${o.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                  {cover ? <img src={cover} alt={o.title} className="w-full h-full object-cover"/>
                         : <div className="w-full h-full grid place-items-center text-slate-300"><ImageIcon size={18}/></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate">{o.title}</div>
                  <div className="text-xs text-slate-500 truncate">{o.business?.name} · {o.city}</div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-slate-700">{o.discount}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      o.status === "published" ? "bg-emerald-100 text-emerald-700" :
                      o.status === "pending" ? "bg-amber-100 text-amber-700" :
                      o.status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"
                    }`}>{o.status}</span>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {o.status !== "published" && (
                  <button onClick={()=>moderate(o.id, "published")} data-testid={`admin-offer-approve-m-${o.id}`}
                          className="flex-1 min-w-[90px] inline-flex items-center justify-center gap-1 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold"><CheckCircle2 size={14}/> Publier</button>
                )}
                {o.status !== "rejected" && (
                  <button onClick={()=>moderate(o.id, "rejected")} data-testid={`admin-offer-reject-m-${o.id}`}
                          className="flex-1 min-w-[90px] inline-flex items-center justify-center gap-1 py-2 rounded-lg bg-amber-500 text-white text-xs font-semibold"><XCircle size={14}/> Refuser</button>
                )}
                {o.status === "published" && (
                  <button onClick={()=>toggleFeature(o)} data-testid={`admin-offer-feature-m-${o.id}`}
                          className={`inline-flex items-center justify-center gap-1 py-2 px-3 rounded-lg text-xs font-semibold border ${o.is_featured ? "text-amber-700 bg-amber-50 border-amber-200" : "text-slate-600 border-slate-200"}`}><Star size={14} className={o.is_featured ? "fill-amber-500" : ""}/> Une</button>
                )}
                {o.status === "published" && (
                  <button onClick={()=>togglePin(o)} data-testid={`admin-offer-pin-dod-m-${o.id}`}
                          className={`inline-flex items-center justify-center gap-1 py-2 px-3 rounded-lg text-xs font-semibold border ${pinnedId === o.id ? "text-[#E05638] bg-orange-50 border-orange-200" : "text-slate-600 border-slate-200"}`}><Flame size={14} className={pinnedId === o.id ? "fill-orange-500" : ""}/> Bon plan</button>
                )}
                <a href={`/offre/${o.id}`} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center justify-center gap-1 py-2 px-3 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold"><Eye size={14}/> Voir</a>
                <button onClick={()=>navigate(`/admin/offres/${o.id}/editer`)} data-testid={`admin-offer-edit-m-${o.id}`}
                        className="inline-flex items-center justify-center gap-1 py-2 px-3 rounded-lg border border-slate-200 text-slate-700 text-xs font-semibold"><Pencil size={14}/> Modifier</button>
                <button onClick={()=>del(o.id)}
                        className="inline-flex items-center justify-center gap-1 py-2 px-3 rounded-lg border border-rose-200 text-rose-600 text-xs font-semibold"><Trash2 size={14}/></button>
              </div>
            </div>
          );
        })}
        {offers.length === 0 && <div className="text-center text-slate-500 p-6">Aucune offre.</div>}
      </div>
    </div>
  );
}
